/** @file Shared app functionality */

import * as jsonexport from 'jsonexport/dist'
import * as Sentry from '@sentry/browser'

import type { DateStr } from './types'

/**
 * Safe way to access Object.hasOwnProperty
 *
 * @param  {object}  obj  - Object to check
 * @param  {string}  prop - Key to check for existence of
 * @returns {boolean} - Whether or not `obj` has a property `key`
 */
export const hasOwnPropertySafe = (obj: Object, prop: string): Boolean =>
  Object.prototype.hasOwnProperty.call(obj, prop)

/**
 * Convert array to object
 *
 * @param  {object[]} arr - Array to convert
 * @returns {object} - Object based on array
 */
export const arrToObj = (arr: { id: string }[]) => arr.reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), {})

/**
 * Sort array of objects based on specific property
 *
 * @param  {string} key - Property to sort by
 * @returns {number} - Sort order
 */
export const sortBy =
  (key: string) =>
  (a: any, b: any): number => {
    let order = 0

    if (hasOwnPropertySafe(a, key) && hasOwnPropertySafe(b, key)) {
      if (a[key] < b[key]) {
        order = -1
      } else if (a[key] > b[key]) {
        order = 1
      }
    }

    return order
  }

/** Add value to object property, creating it if it doesn't exist */
export const addCreate = (obj: object, key: string, val: number) => {
  if (obj[key]) {
    obj[key] += val
  } else {
    obj[key] = val
  }
}

/**
 * Format a passed in number (adds commas and truncates decimal places)
 *
 * @param  {number} number        - Number to format
 * @param  {[number]} decimalPlaces - Number of decimal places to allow
 * @returns {string} - Formatted number (as string)
 */
export const formatNumber = (number: number, decimalPlaces: number = 0): string =>
  new Intl.NumberFormat('en-US', {
    maximumFractionDigits: decimalPlaces
  }).format(number)

/**
 * Convert date string in local time zone (eg: YYYY-MM-DD) to Date
 * The parameter values (year, month, day) are all evaluated against the local time zone, rather than UTC
 *
 * @param  {string} dateStr - String to convert
 * @returns {Date} - String converted to date
 */
export const dateStringToDate = (dateStr: DateStr): Date => {
  const [y, m, d] = dateStr.split('-')
  return new Date(Number(y), Number(m) - 1, Number(d))
}

/**
 * Convert a standard date to a date string
 *
 * @param {Date} date - Date
 * @returns {string} - Date string
 */
export const dateToDateString = (date: Date): DateStr => {
  const yStr = date.getFullYear()
  const mStr = (date.getMonth() + 1 < 10 ? '0' : '') + (date.getMonth() + 1)
  const dStr = (date.getDate() < 10 ? '0' : '') + date.getDate()

  return `${yStr}-${mStr}-${dStr}`
}

/**
 * Convert a timestamp from the server into a Date
 *
 * @param {number} timestamp - Timestamp
 * @returns {Date} - JS Date version of timestamp
 */
export const timestampToDate = (timestamp: number) => new Date(timestamp * 1000)

/**
 * Convert an array to a CSV string
 *
 * @param  {object[]} arr - Array of objects
 * @returns {Promise} - Promise that resolves to CSV string
 */
export const convertArrayToCsvString = (arr: Object[]): Promise<string> =>
  new Promise((resolve, reject) => {
    jsonexport(arr, function (err, csv) {
      if (err) {
        return reject(err)
      }
      return resolve(csv)
    })
  })

/**
 * Wrapper for `console` that pushes messages to external systems
 */
export const log = {
  ...console,
  info: (category: string, message: string, data?: Object, level: Sentry.Severity = Sentry.Severity.Info) => {
    Sentry.addBreadcrumb({ category, message, data, level })
    console.info({ category, message, data, level })
  },
  error: (...args: any[]) => {
    Sentry.captureException(args)
    console.error(args)
  }
}
